/**
 * `/$tenantSlug/settings/organization` — Organization Settings.
 *
 * Per ADR-0078 + the org_settings_and_print plan (Wave 1). The owner-
 * editable company identity that every printed/mailed financial document
 * needs: legal name, addresses, remit-to, contact, tax registration, logo,
 * and document defaults. Writes to `tenants.branding` JSONB (+ currency,
 * a real column) through the settings-role SDF behind
 * PUT /api/shell/settings/organization (gated shell.settings.edit).
 *
 * Archetype: "config" (design-intent §A) — header only, NO hero/gradient.
 *
 * Design-intent compliance:
 *   - §G: sectioned <form> with <fieldset>+<legend> per section; real
 *     labels (FormField); aria-pressed Switches with a live-region
 *     announce when they hide/show fields; save success in a role=status
 *     live region; focus is NOT stolen on success and moves to the alert
 *     on failure; 40px inputs/buttons; AA token pairs + glyphs; 12px floor.
 *   - §D: errors use the AA --color-status-overdue-* pair (FormField does
 *     this), NEVER the raw --color-accent-red the old settings pages use.
 *   - §E: this page has no side-effecting verbs beyond Save (a committed
 *     feature, live here).
 *
 * v1 is owner-typed across all fields (ADR-0078 §5 — QuickBooks captures
 * no company identity today). No provenance chips render because the
 * backend's `derived.from_quickbooks` is always empty in v1; the
 * FormField `fromQuickbooks` prop is wired for the named fast-follow.
 */

import { RequirePermission, useCurrency, usePermission } from "@ken/auth";
import {
  AddressGroup,
  EMPTY_ADDRESS,
  FormField,
  Switch,
  type AddressValue,
  type FormFieldRenderArgs,
} from "@ken/forms";
import {
  Button,
  Card,
  CardContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from "@ken/ui";
import { createFileRoute } from "@tanstack/react-router";
import { Building2, Clock } from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactElement,
  type ReactNode,
} from "react";

export const Route = createFileRoute("/$tenantSlug/settings/organization")({
  component: OrganizationSettingsRoute,
});

/**
 * Hard permission gate (defense-in-depth): the parent /settings index only
 * HIDES the card for users without `shell.settings.view`; it does not block
 * direct navigation to this child route. Without this wrapper an advisor (or
 * any user lacking the permission) who navigates straight to the URL gets the
 * form shell. RequirePermission renders an accessible "no access" region
 * instead — mirroring the approvals route. ADR-0090 (advisor envelope).
 */
function OrganizationSettingsRoute(): ReactElement {
  return (
    <RequirePermission slug="shell.settings.view">
      <OrganizationSettingsContent />
    </RequirePermission>
  );
}

// ---------------------------------------------------------------------------
// Backend contract (ADR-0078) — consumed, not built here.
// ---------------------------------------------------------------------------

interface TaxRegistration {
  number: string;
  registered: boolean;
  kind: string | null;
}

interface Branding {
  legal_name: string;
  dba: string;
  logo_url: string;
  address: AddressValue;
  remit_to: AddressValue;
  remit_same: boolean;
  phone: string;
  email: string;
  tax_registration: TaxRegistration;
  default_terms_id: string | null;
  date_format: string;
  fiscal_year_start: string;
}

interface OrganizationResponse {
  branding: Partial<Branding>;
  currency: string;
  derived: { from_quickbooks: string[] };
}

interface TermProfile {
  id: string;
  name: string;
}

const DATE_FORMATS = [
  { value: "YYYY-MM-DD", label: "2026-03-01 (YYYY-MM-DD)" },
  { value: "MM/DD/YYYY", label: "03/01/2026 (MM/DD/YYYY)" },
  { value: "DD/MM/YYYY", label: "01/03/2026 (DD/MM/YYYY)" },
  { value: "DD-MMM-YYYY", label: "01-Mar-2026 (DD-MMM-YYYY)" },
] as const;

const TAX_KINDS = ["GST", "HST", "VAT", "None"] as const;

const MONTHS = [
  ["01-01", "January"],
  ["02-01", "February"],
  ["03-01", "March"],
  ["04-01", "April"],
  ["05-01", "May"],
  ["06-01", "June"],
  ["07-01", "July"],
  ["08-01", "August"],
  ["09-01", "September"],
  ["10-01", "October"],
  ["11-01", "November"],
  ["12-01", "December"],
] as const;

function emptyBranding(): Branding {
  return {
    legal_name: "",
    dba: "",
    logo_url: "",
    address: { ...EMPTY_ADDRESS },
    remit_to: { ...EMPTY_ADDRESS },
    remit_same: false,
    phone: "",
    email: "",
    tax_registration: { number: "", registered: false, kind: null },
    default_terms_id: null,
    date_format: "YYYY-MM-DD",
    fiscal_year_start: "01-01",
  };
}

function normalize(b: Partial<Branding> | undefined): Branding {
  const base = emptyBranding();
  if (!b) return base;
  return {
    ...base,
    ...b,
    address: { ...base.address, ...(b.address ?? {}) },
    remit_to: { ...base.remit_to, ...(b.remit_to ?? {}) },
    tax_registration: { ...base.tax_registration, ...(b.tax_registration ?? {}) },
  };
}

function isEmptyBranding(b: Branding): boolean {
  return (
    !b.legal_name &&
    !b.dba &&
    !b.logo_url &&
    !b.phone &&
    !b.email &&
    !b.tax_registration.number &&
    !Object.values(b.address).some(Boolean) &&
    !Object.values(b.remit_to).some(Boolean)
  );
}

function OrganizationSettingsContent(): ReactElement {
  // canView is guaranteed true here (the route is wrapped in
  // <RequirePermission slug="shell.settings.view">). Kept for the fetch guard.
  const canView = usePermission("shell.settings.view");
  const canEdit = usePermission("shell.settings.edit");
  const sessionCurrency = useCurrency();

  const [branding, setBranding] = useState<Branding>(emptyBranding());
  const [savedBranding, setSavedBranding] = useState<Branding>(emptyBranding());
  const [currency, setCurrency] = useState<string>(sessionCurrency);
  const [savedCurrency, setSavedCurrency] = useState<string>(sessionCurrency);
  const [terms, setTerms] = useState<TermProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string>("");

  const errorRef = useRef<HTMLDivElement | null>(null);

  // Load org + terms.
  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const [orgRes, termsRes] = await Promise.all([
          fetch("/api/shell/settings/organization", { credentials: "include" }),
          fetch("/api/settings/terms?filter_status=all", { credentials: "include" }),
        ]);
        if (cancelled) return;
        if (orgRes.status === 403) {
          setError("You don't have permission to view organization settings.");
          return;
        }
        if (!orgRes.ok) {
          setError(`Couldn't load organization settings (status ${orgRes.status}).`);
          return;
        }
        const body = (await orgRes.json()) as OrganizationResponse;
        const normalized = normalize(body.branding);
        if (cancelled) return;
        setBranding(normalized);
        setSavedBranding(normalized);
        setCurrency(body.currency || sessionCurrency);
        setSavedCurrency(body.currency || sessionCurrency);
        if (termsRes.ok) {
          const tbody = (await termsRes.json()) as { items: TermProfile[] };
          if (!cancelled) setTerms(tbody.items ?? []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Network error.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canView, sessionCurrency]);

  const dirty = useMemo(
    () =>
      JSON.stringify(branding) !== JSON.stringify(savedBranding) ||
      currency !== savedCurrency,
    [branding, savedBranding, currency, savedCurrency],
  );

  const firstRun = useMemo(() => isEmptyBranding(savedBranding), [savedBranding]);
  const readOnly = canView && !canEdit;

  function patch(next: Partial<Branding>): void {
    setBranding((b) => ({ ...b, ...next }));
  }

  function toggleRemitSame(next: boolean): void {
    patch({ remit_same: next });
    setStatusMessage(
      next
        ? "Remit-to address set to match the business address."
        : "Remit-to address can now be entered separately.",
    );
  }

  function toggleTaxRegistered(next: boolean): void {
    patch({
      tax_registration: { ...branding.tax_registration, registered: next },
    });
    setStatusMessage(
      next
        ? "Tax registration enabled. Choose a tax type and enter your number."
        : "Tax registration disabled.",
    );
  }

  async function save(): Promise<void> {
    if (!canEdit || saving) return;
    setSaving(true);
    setError(null);
    // Optimistic: flip saved snapshot immediately so the dirty pill clears;
    // roll back on failure.
    const prevSavedBranding = savedBranding;
    const prevSavedCurrency = savedCurrency;
    const optimistic = branding;
    const optimisticCurrency = currency;
    setSavedBranding(optimistic);
    setSavedCurrency(optimisticCurrency);

    try {
      const res = await fetch("/api/shell/settings/organization", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...optimistic, currency: optimisticCurrency }),
      });
      if (res.status === 403) {
        setSavedBranding(prevSavedBranding);
        setSavedCurrency(prevSavedCurrency);
        setError("You don't have permission to edit organization settings.");
        return;
      }
      if (res.status === 422) {
        setSavedBranding(prevSavedBranding);
        setSavedCurrency(prevSavedCurrency);
        setError("Some values were rejected. Check the fields and try again.");
        return;
      }
      if (!res.ok) {
        setSavedBranding(prevSavedBranding);
        setSavedCurrency(prevSavedCurrency);
        setError(`Couldn't save (status ${res.status}).`);
        return;
      }
      // Success: snapshot already updated; announce without stealing focus.
      setStatusMessage("Saved.");
    } catch (err) {
      setSavedBranding(prevSavedBranding);
      setSavedCurrency(prevSavedCurrency);
      setError(err instanceof Error ? err.message : "Network error.");
    } finally {
      setSaving(false);
    }
  }

  // On error, move focus to the alert (§G — focus moves to the alert on
  // error; never steals focus on success).
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.focus();
    }
  }, [error]);

  return (
    <div className="flex flex-col gap-6" data-testid="shell-settings-organization-page">
      <header>
        <div className="text-[12px] uppercase tracking-[0.08em] text-[var(--color-text-faint)] font-semibold">
          Tenant · Settings
        </div>
        <h1 className="text-[26px] font-bold tracking-tight">Organization</h1>
        <p className="text-sm text-[var(--color-text-soft)] mt-1">
          Your business identity — the details that appear on every invoice,
          bill, and statement you print or send.
        </p>
      </header>

      {/* Live region — announces toggles + save success without stealing focus. */}
      <div
        data-testid="shell-settings-organization-live"
        role="status"
        aria-live="polite"
        className="sr-only"
      >
        {statusMessage}
      </div>

      {error !== null && (
        <div
          ref={errorRef}
          tabIndex={-1}
          role="alert"
          data-testid="shell-settings-organization-error"
          className="text-sm text-[var(--color-status-overdue-text)] px-3 py-2 rounded-[var(--radius-sm)] bg-[var(--color-status-overdue-bg)] border border-[var(--color-status-overdue-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-status-overdue-text)]"
        >
          {error}
        </div>
      )}

      {readOnly && (
        <div
          data-testid="shell-settings-organization-readonly-note"
          className="text-[13px] text-[var(--color-text-soft)] px-3 py-2 rounded-[var(--radius-sm)] bg-[var(--color-surface-soft)] border border-[var(--color-border-hairline)]"
        >
          You can view these settings but not change them. Ask an owner or admin
          to edit your organization details.
        </div>
      )}

      {loading ? (
        <div data-testid="shell-settings-organization-loading" className="flex flex-col gap-3">
          <Skeleton className="h-40 w-full rounded-[var(--radius-md)]" />
          <Skeleton className="h-40 w-full rounded-[var(--radius-md)]" />
        </div>
      ) : (
        <>
          {firstRun && !readOnly && (
            <div
              data-testid="shell-settings-organization-intro"
              className="rounded-[var(--radius-md)] border border-[var(--color-brand-soft)] bg-[var(--color-brand-soft)]/40 px-4 py-3"
            >
              <div className="flex items-start gap-3">
                <Building2 size={18} className="mt-0.5 text-[var(--color-brand-strong)]" aria-hidden="true" />
                <div>
                  <div className="text-sm font-semibold text-[var(--color-text-strong)]">
                    Set up your business details
                  </div>
                  <p className="text-[13px] text-[var(--color-text-soft)] mt-0.5">
                    Add your legal name, address, and contact info so your
                    invoices are ready to mail. We&apos;ll auto-fill these from
                    QuickBooks in a later update.
                  </p>
                </div>
              </div>
            </div>
          )}

          <form
            data-testid="shell-settings-organization-form"
            onSubmit={(e) => {
              e.preventDefault();
              void save();
            }}
            className="flex flex-col gap-5 pb-24"
          >
            {/* A — Business identity */}
            <Section testId="identity" legend="Business identity">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FormField
                  label="Legal name"
                  value={branding.legal_name}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => patch({ legal_name: e.target.value })}
                  readOnly={readOnly}
                  data-testid="shell-settings-organization-field-legal_name"
                />
                <FormField
                  label="DBA (doing business as)"
                  value={branding.dba}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => patch({ dba: e.target.value })}
                  readOnly={readOnly}
                  data-testid="shell-settings-organization-field-dba"
                />
                <FormField
                  label="Logo URL"
                  type="url"
                  inputMode="url"
                  placeholder="https://…"
                  hint="Paste a link to your logo image. Uploads come in a later update."
                  value={branding.logo_url}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => patch({ logo_url: e.target.value })}
                  readOnly={readOnly}
                  data-testid="shell-settings-organization-field-logo_url"
                  className="sm:col-span-2"
                />
              </div>
            </Section>

            {/* B — Addresses */}
            <Section testId="addresses" legend="Addresses">
              <fieldset className="border-0 p-0 m-0">
                <legend className="text-[12px] font-semibold text-[var(--color-text-strong)] mb-2">
                  Business address
                </legend>
                <AddressGroup
                  value={branding.address}
                  onChange={(address: AddressValue) => patch({ address })}
                  testIdPrefix="shell-settings-organization-field-business_address"
                  readOnly={readOnly}
                />
              </fieldset>

              <div className="flex items-center gap-3 mt-4">
                <Switch
                  checked={branding.remit_same}
                  onCheckedChange={readOnly ? () => {} : toggleRemitSame}
                  disabled={readOnly}
                  aria-label="Remit-to address is the same as the business address"
                  data-testid="shell-settings-organization-remit-same-toggle"
                />
                <span className="text-[13px] text-[var(--color-text-strong)]">
                  Remit-to address is the same as the business address
                </span>
              </div>

              {!branding.remit_same && (
                <fieldset className="border-0 p-0 m-0 mt-3">
                  <legend className="text-[12px] font-semibold text-[var(--color-text-strong)] mb-2">
                    Remit-to address
                  </legend>
                  <AddressGroup
                    value={branding.remit_to}
                    onChange={(remit_to: AddressValue) => patch({ remit_to })}
                    testIdPrefix="shell-settings-organization-field-remit_to_address"
                    readOnly={readOnly}
                  />
                </fieldset>
              )}
            </Section>

            {/* C — Contact */}
            <Section testId="contact" legend="Contact">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FormField
                  label="Phone"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={branding.phone}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => patch({ phone: e.target.value })}
                  readOnly={readOnly}
                  data-testid="shell-settings-organization-field-phone"
                />
                <FormField
                  label="Email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  value={branding.email}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => patch({ email: e.target.value })}
                  readOnly={readOnly}
                  data-testid="shell-settings-organization-field-email"
                />
              </div>
            </Section>

            {/* D — Tax & compliance */}
            <Section testId="tax" legend="Tax & compliance">
              <div className="flex items-center gap-3">
                <Switch
                  checked={branding.tax_registration.registered}
                  onCheckedChange={readOnly ? () => {} : toggleTaxRegistered}
                  disabled={readOnly}
                  aria-label="My business is registered for sales tax"
                  data-testid="shell-settings-organization-tax-registered-toggle"
                />
                <span className="text-[13px] text-[var(--color-text-strong)]">
                  My business is registered for sales tax
                </span>
              </div>

              {branding.tax_registration.registered && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <LabeledSelect
                    label="Tax type"
                    testId="shell-settings-organization-select-tax_kind"
                    value={branding.tax_registration.kind ?? ""}
                    placeholder="Select a tax type"
                    disabled={readOnly}
                    options={TAX_KINDS.map((k) => ({ value: k, label: k }))}
                    onValueChange={(v) =>
                      patch({
                        tax_registration: {
                          ...branding.tax_registration,
                          kind: v === "None" ? null : v,
                        },
                      })
                    }
                  />
                  <FormField
                    label="Registration number"
                    value={branding.tax_registration.number}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      patch({
                        tax_registration: {
                          ...branding.tax_registration,
                          number: e.target.value,
                        },
                      })
                    }
                    readOnly={readOnly}
                    data-testid="shell-settings-organization-field-tax_registration_number"
                  />
                </div>
              )}
            </Section>

            {/* E — Document defaults */}
            <Section testId="defaults" legend="Document defaults">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <LabeledSelect
                  label="Default payment terms"
                  testId="shell-settings-organization-select-default_payment_terms"
                  value={branding.default_terms_id ?? ""}
                  placeholder={terms.length ? "Select terms" : "No terms yet"}
                  disabled={readOnly || terms.length === 0}
                  options={terms.map((t) => ({ value: t.id, label: t.name }))}
                  onValueChange={(v) => patch({ default_terms_id: v || null })}
                />
                <LabeledSelect
                  label="Date format"
                  testId="shell-settings-organization-select-date_format"
                  value={branding.date_format}
                  disabled={readOnly}
                  options={DATE_FORMATS.map((d) => ({ value: d.value, label: d.label }))}
                  onValueChange={(v) => patch({ date_format: v })}
                />
                <LabeledSelect
                  label="Fiscal year starts"
                  testId="shell-settings-organization-select-fiscal_year_start"
                  value={branding.fiscal_year_start}
                  disabled={readOnly}
                  options={MONTHS.map(([v, l]) => ({ value: v, label: l }))}
                  onValueChange={(v) => patch({ fiscal_year_start: v })}
                />
              </div>
            </Section>
          </form>

          {/* Sticky SaveBar — only for editors. */}
          {!readOnly && (
            <div
              data-testid="shell-settings-organization-savebar"
              className="fixed bottom-0 right-0 left-0 sm:left-[var(--sidebar-w,260px)] z-20 flex items-center justify-end gap-3 border-t border-[var(--color-border-hairline)] bg-[var(--color-surface)]/95 backdrop-blur px-6 py-3"
            >
              {dirty && (
                <span
                  data-testid="shell-settings-organization-dirty-pill"
                  className="inline-flex items-center gap-1 rounded-[var(--radius-full)] border border-transparent bg-[var(--color-status-trial-bg)] px-2.5 py-1 text-[12px] font-medium text-[var(--color-status-trial-text)]"
                >
                  <Clock size={12} aria-hidden="true" />
                  Unsaved changes
                </span>
              )}
              <Button
                type="button"
                variant="primary"
                size="md"
                disabled={!dirty || saving}
                data-testid="shell-settings-organization-save"
                onClick={() => void save()}
              >
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Section({
  testId,
  legend,
  children,
}: {
  testId: string;
  legend: string;
  children: ReactNode;
}): ReactElement {
  return (
    <Card surface="glass-strong" data-testid={`shell-settings-organization-section-${testId}`}>
      <CardContent>
        <fieldset className="border-0 p-0 m-0">
          <legend className="text-[14px] font-semibold text-[var(--color-text-strong)] mb-3">
            {legend}
          </legend>
          {children}
        </fieldset>
      </CardContent>
    </Card>
  );
}

function LabeledSelect({
  label,
  testId,
  value,
  placeholder,
  options,
  onValueChange,
  disabled,
}: {
  label: string;
  testId: string;
  value: string;
  placeholder?: string;
  options: Array<{ value: string; label: string }>;
  onValueChange: (value: string) => void;
  disabled?: boolean;
}): ReactElement {
  return (
    <FormField
      label={label}
      render={({ id, describedBy }: FormFieldRenderArgs) => (
        <Select
          // exactOptionalPropertyTypes: omit `value` entirely when empty
          // rather than passing `undefined` (Radix treats absent === uncontrolled-empty).
          {...(value ? { value } : {})}
          onValueChange={onValueChange}
        >
          <SelectTrigger
            id={id}
            aria-describedby={describedBy}
            aria-label={label}
            data-testid={testId}
            disabled={Boolean(disabled)}
          >
            <SelectValue placeholder={placeholder ?? "Select…"} />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    />
  );
}
